import React, { useState } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import {
    SectionHeader,
    CardGlass,
    Button,
    Badge
} from "@/components/ui/PremiumComponents";
import ConexoLogo from "@/components/ui/ConexoLogo";
import {
    ShieldCheck,
    ToggleRight,
    Zap,
    Globe,
    Smartphone,
    HardDrive,
    Bell,
    Lock,
    Cpu,
    Save
} from 'lucide-react';
import { toast } from "sonner";

export default function SuperAdminSettings() {
    const [saving, setSaving] = useState(false);
    const [features, setFeatures] = useState({
        qr: true,
        delivery: true,
        multiPrinter: false,
        staffDrivers: true,
        multiBranch: true
    });

    const toggleFeature = (f) => setFeatures(prev => ({ ...prev, [f]: !prev[f] }));

    const handleSave = () => {
        setSaving(true);
        setTimeout(() => {
            setSaving(false);
            toast.success("Configuración global actualizada con éxito.");
        }, 1500);
    };

    return (
        <SuperAdminLayout>
            <div className="relative pb-24">
                <SectionHeader
                    title="Configuración Global"
                    subtitle="Control total sobre la marca, características del producto y seguridad del ecosistema."
                    actions={
                        <div className="hidden md:flex gap-3">
                            <Badge status="success">Versión 2.4.8-STABLE</Badge>
                        </div>
                    }
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Section: Branding */}
                    <CardGlass title="Identidad Visual" subtitle="Logo y apariencia del sistema Conexo OS.">
                        <div className="mt-8 space-y-8">
                            <div className="flex flex-col gap-4">
                                <label className="text-[10px] font-black uppercase tracking-wider" style={{ color: 'var(--text-soft)' }}>Preview del Logo</label>
                                <div className="p-10 bg-white/5 rounded-3xl border border-white/10 flex items-center justify-center relative overflow-hidden group">
                                    <ConexoLogo size={42} className="transform transition-transform group-hover:scale-110" />
                                    <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-accent/5 opacity-50" />
                                </div>
                                <div className="flex gap-4">
                                    <Button variant="secondary" className="flex-1 text-xs">Cambiar Isotipo</Button>
                                    <Button variant="secondary" className="flex-1 text-xs">Cambiar Texto</Button>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-black uppercase tracking-wider" style={{ color: 'var(--text-soft)' }}>Nombre del Producto</label>
                                    <input
                                        type="text"
                                        defaultValue="Conexo POS"
                                        className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white font-bold focus:ring-1 ring-primary/40 outline-none"
                                    />
                                </div>
                            </div>
                        </div>
                    </CardGlass>

                    {/* Section: Features */}
                    <CardGlass title="Módulos & Features" subtitle="Activa o desactiva funcionalidades a nivel global.">
                        <div className="mt-8 space-y-4">
                            {[
                                { id: 'qr', label: 'Pedidos vía QR (Mesa)', desc: 'Permite a los clientes pedir desde su smartphone.', icon: <Smartphone size={18} /> },
                                { id: 'delivery', label: 'Módulo Delivery/Takeout', desc: 'Gestión de pedidos externos y tiempos.', icon: <Zap size={18} /> },
                                { id: 'staffDrivers', label: 'Repartidores Propios', desc: 'Sistema de seguimiento de flota interna.', icon: <Globe size={18} /> },
                                { id: 'multiBranch', label: 'Gestión Multisucursal', desc: 'Soporte para múltiples sedes por tenant.', icon: <HardDrive size={18} /> },
                                { id: 'multiPrinter', label: 'Routing de Impresión Pro', desc: 'Enrutado avanzado a múltiples destinos.', icon: <Cpu size={18} /> },
                            ].map(f => (
                                <div key={f.id} className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/5 transition-all hover:bg-white/10">
                                    <div className="flex gap-4 items-center">
                                        <div className="p-2.5 bg-primary/10 rounded-xl text-primary">{f.icon}</div>
                                        <div>
                                            <div className="text-sm font-bold text-white">{f.label}</div>
                                            <div className="text-[10px] font-medium" style={{ color: 'var(--text-soft)' }}>{f.desc}</div>
                                            {f.id === 'delivery' && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); window.location.href = '/superadmin/settings/delivery'; }}
                                                    className="mt-2 text-[10px] font-black uppercase tracking-widest text-primary hover:underline"
                                                >
                                                    Configurar Tarifas →
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => toggleFeature(f.id)}
                                        className={`w-12 h-6 rounded-full relative p-1 transition-all duration-300 ${features[f.id] ? 'bg-primary' : 'bg-white/10'}`}
                                    >
                                        <div className={`w-4 h-4 bg-white rounded-full shadow-lg transition-all duration-300 ${features[f.id] ? 'translate-x-6' : 'translate-x-0'}`} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </CardGlass>

                    {/* Section: Operation */}
                    <CardGlass title="Operación & Límites" subtitle="Control operativo de la plataforma SaaS.">
                        <div className="mt-8 space-y-6">
                            <div className="flex items-center justify-between p-4 bg-red-500/5 rounded-2xl border border-red-500/10">
                                <div className="flex gap-4 items-center">
                                    <div className="p-2.5 bg-red-500/10 rounded-xl text-red-500"><Bell size={18} /></div>
                                    <div>
                                        <div className="text-sm font-bold text-white">Modo Mantenimiento</div>
                                        <div className="text-[10px] text-red-400 font-medium">Bloquea el acceso a todos los dashboards.</div>
                                    </div>
                                </div>
                                <button className="w-12 h-6 bg-white/10 rounded-full relative p-1"><div className="w-4 h-4 bg-white/40 rounded-full" /></button>
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] font-black uppercase tracking-wider" style={{ color: 'var(--text-soft)' }}>Banner Global de Notificación</label>
                                <textarea
                                    placeholder="Escribe un mensaje para todos los terminales..."
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-xs text-white h-24 outline-none focus:ring-1 ring-primary/40"
                                />
                            </div>
                        </div>
                    </CardGlass>

                    {/* Section: Security */}
                    <CardGlass title="Seguridad Avanzada" subtitle="Hardening y control de accesos.">
                        <div className="mt-8 space-y-6">
                            <div className="flex items-center justify-between">
                                <div className="flex gap-4">
                                    <div className="p-2.5 bg-primary/10 rounded-xl text-primary"><Lock size={18} /></div>
                                    <div>
                                        <div className="text-sm font-bold text-white">Autenticación 2FA</div>
                                        <div className="text-[10px]" style={{ color: 'var(--text-soft)' }}>Requerir MFA para acceder al SuperAdmin.</div>
                                    </div>
                                </div>
                                <Badge status="info">Sugerido</Badge>
                            </div>

                            <div className="space-y-4">
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-black uppercase tracking-wider" style={{ color: 'var(--text-soft)' }}>IP Allowlist (Admin)</label>
                                    <input
                                        type="text"
                                        placeholder="192.168.1.1, 8.8.8.8..."
                                        className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white font-mono"
                                    />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-black uppercase tracking-wider" style={{ color: 'var(--text-soft)' }}>Rate Limits (API Requests/min)</label>
                                    <input
                                        type="number"
                                        defaultValue={100}
                                        className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white font-mono w-32"
                                    />
                                </div>
                            </div>

                            <div className="pt-4 border-t" style={{ borderColor: 'var(--border-soft)' }}>
                                <div className="flex gap-2 items-center text-primary">
                                    <ShieldCheck size={14} />
                                    <span className="text-[10px] font-black tracking-widest uppercase">Certificados SSL Activos</span>
                                </div>
                            </div>
                        </div>
                    </CardGlass>
                </div>

                {/* Global Sticky Save Button */}
                <div className="fixed bottom-10 right-10 z-50 animate-bounce-slow">
                    <Button
                        variant="primary"
                        className="flex items-center gap-3 px-8 py-5 rounded-3xl shadow-2xl scale-110"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <Save size={20} />
                        )}
                        <span className="text-base">Guardar Cambios</span>
                    </Button>
                </div>
            </div>
        </SuperAdminLayout>
    );
}
